import { getApiBase } from "../utils/apiBase";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiResponse, ApiError } from '../types/api';

export interface FilestoreFile {
  id: number;
  s3_path: string;
  original_filename: string;
  file_size: number;
  content_type: string;
  upload_date: string;
  uploaded_from: string | null;
  is_public: boolean;
  username: string;
}

export const useFilestoreFiles = (search?: string) => {
  const queryParams = new URLSearchParams();
  if (search) {
    queryParams.append('search', search);
  }
  
  const queryString = queryParams.toString();
  const url = `/api/filestore${queryString ? `?${queryString}` : ''}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['filestore', search],
    queryFn: () => api.request<ApiResponse<{ data: FilestoreFile[] }>>(url),
    refetchOnWindowFocus: false,
  }) as { data: ApiResponse<{ data: FilestoreFile[] }>; isLoading: boolean; error: ApiError };

  return { 
    files: data?.data?.data ?? [], 
    isLoading, 
    error 
  };
};

export const useUploadToFilestore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, uploadedFrom }: { file: File; uploadedFrom?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (uploadedFrom) {
        formData.append('uploaded_from', uploadedFrom);
      }

      return api.request<ApiResponse<FilestoreFile>>('/api/filestore', {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filestore'] });
    },
  });
};

export const parseFilestoreFile = async (fileId: number): Promise<any> => {
  const response = await api.request<ApiResponse<{ data: any }>>(
    `/api/filestore/${fileId}/parse`
  );
  return response?.data?.data ?? response?.data;
};

export const useUpdateFilestoreFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, isPublic }: { fileId: number; isPublic: boolean }) => {
      return api.request(`/api/filestore/${fileId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_public: isPublic }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filestore'] });
    },
  });
};

export const useDeleteFilestoreFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: number) => {
      await api.request(`/api/filestore/${fileId}`, {
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: ['filestore'] });
    },
  });
};

export const analyzeFileForLines = async (fileId: number): Promise<{ hasLines: boolean; fields: string[] }> => {
  const response = await api.request<ApiResponse<{ data: { hasLines: boolean; fields: string[] } }>>(
    `/api/filestore/${fileId}/line-analysis`
  );
  return response?.data?.data ?? response?.data;
};

export const downloadFilestoreFile = async (fileId: number, filename: string) => {
  try {
    // Use fetch directly to handle binary response with proper base URL
    const response = await fetch(`${getApiBase()}/api/filestore/${fileId}`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    
    // Create blob from response
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error('Failed to download file');
  }
};
